import { useAsyncState } from '@vueuse/core'
import cloneDeep from 'lodash/cloneDeep'
import {
  AfirData,
  ElectricityGridOwner,
  VehicleCountData,
  PopulationData,
  VehicleCountDataOnPopulation,
  VehicleTypeCountData,
  FuelTypeCount,
} from './../types/statistics'

import { useStatisticsStore } from '~/stores/statistics'
import { RegionType } from '@/types/types'
import { Filter } from '~/types/filters'
import { useStationDataStore } from '~/stores/stations'

const dataUrls: { [key: string]: string } = {
  vehicleCount: 'vehicle/count',
  vehicleForecast: 'vehicle/forecast',
  population: 'population',
  electricityGrids: 'electricity-grids',
}

export function useStatisticsDataFetch<T>(
  dataType: 'vehicleCount' | 'population' | 'electricityGrids' | 'vehicleForecast',
  region?: string,
  regionId?: string,
  getAll?: boolean
) {
  const dataUrl = dataUrls[dataType]
  const client = useStrapiClient()
  const statisticsStore = useStatisticsStore()

  let endpoint = ''

  if (
    dataType === 'vehicleCount' ||
    dataType === 'population' ||
    dataType === 'electricityGrids' ||
    dataType === 'vehicleForecast'
  ) {
    if (!region && !regionId) {
      endpoint = `/statistics/${dataUrl}${getAll ? '/all' : ''}`
    } else if (region && !regionId) {
      endpoint = `/statistics/${dataUrl}/${region}${getAll ? '/all' : ''}`
    } else {
      endpoint = `/statistics/${dataUrl}/${region}/${regionId}${getAll ? '/all' : ''}`
    }
  }
  const existCache = statisticsStore.cachedStatistics[endpoint]

  if (existCache && existCache.data) {
    const isReady = ref(true)
    const isLoading = ref(false)
    const data = ref(cloneDeep(existCache.data))
    return {
      data,
      isReady,
      isLoading,
    }
  }

  statisticsStore.cachedStatistics[endpoint] = { data: null }
  const { state, isReady, isLoading } = useAsyncState(async () => {
    const response = await client<T[]>(endpoint, { method: 'GET' })
    let returnPayload: T[] | T | null = null
    if (dataType === 'electricityGrids') returnPayload = response
    if (Array.isArray(response)) returnPayload = response.length > 1 ? response : response[0]
    return returnPayload
  }, null)

  watch(state, (data) => {
    statisticsStore.cachedStatistics[endpoint].data = data
  })

  return {
    data: state,
    isReady,
    isLoading,
  }
}

export function usePopulationDataFetch<T>(
  region?: RegionType,
  regionId?: string,
  getAll?: boolean
) {
  const { data, ...rest } = useStatisticsDataFetch<T>('population', region, regionId, getAll)
  const PopulationData = data as Ref<T>
  return {
    data: PopulationData,
    ...rest,
  }
}

export function useVehicleForecastDataFetch(
  region?: RegionType,
  regionId?: string,
  getAll?: boolean
) {
  const { data: vehicleForecastData, ...rest } = useStatisticsDataFetch<VehicleCountData>(
    'vehicleForecast',
    region,
    regionId,
    getAll
  )

  const vehicleData = vehicleForecastData as Ref<VehicleCountData[]>
  const statisticsStore = useStatisticsStore()
  const filters = computed(() => statisticsStore.filters)
  const filteredData = ref(vehicleForecastData ? vehicleData.value : null)

  watch(vehicleData, () => {
    if (!Array.isArray(vehicleData.value) && vehicleData.value)
      vehicleData.value = [vehicleData.value]
    filterVehicleCountData(vehicleData, filteredData, filters)
    summariseVehicleCountData(filteredData)
    summariseVehicleCountData(vehicleData)
  })

  watch(statisticsStore.filters, () => {
    filterVehicleCountData(vehicleData, filteredData, filters)
    summariseVehicleCountData(filteredData)
    summariseVehicleCountData(vehicleData)
  })

  return {
    data: vehicleForecastData,
    filteredData,
    ...rest,
  }
}

export function useVehicleCountDataFetch<T>(
  region?: RegionType,
  regionId?: string,
  getAll?: boolean
) {
  const { data, ...rest } = useStatisticsDataFetch<T>('vehicleCount', region, regionId, getAll)
  const vehicleData = data as Ref<VehicleCountData[]>
  const statisticsStore = useStatisticsStore()
  const filters = computed(() => statisticsStore.filters)
  const filteredData = ref(data ? vehicleData.value : null)

  watch(vehicleData, () => {
    if (!Array.isArray(vehicleData.value) && vehicleData.value)
      vehicleData.value = [vehicleData.value]
    filterVehicleCountData(vehicleData, filteredData, filters)
    summariseVehicleCountData(filteredData)
    summariseVehicleCountData(vehicleData)
  })

  watch(statisticsStore.filters, () => {
    filterVehicleCountData(vehicleData, filteredData, filters)
    summariseVehicleCountData(filteredData)
    summariseVehicleCountData(vehicleData)
  })
  return {
    data: vehicleData,
    filteredData,
    ...rest,
  }
}

function filterVehicleCountData(
  vehicleData: Ref<VehicleCountData[]>,
  filteredData: Ref<VehicleCountData[] | null>,
  filters: Ref<Filter[]>
) {
  if (!vehicleData.value) return
  const dataClone = cloneDeep(vehicleData.value)
  const dataItems = Array.isArray(dataClone) ? dataClone : [dataClone]
  const vehicleTypeFilter = filters.value.find(
    (filter) => filter.filterIdentifier === 'vehicleTypeFromFleetData'
  )
  const filterValues = vehicleTypeFilter?.values || []

  if (!filterValues.length) {
    filteredData.value = dataItems
    return
  }

  const filteredDataItems = dataItems.map((dataItem) => {
    dataItem.vehicle_data = dataItem.vehicle_data.filter((vehicleDataItem) => {
      return filterValues.includes(vehicleDataItem.vehicle_type)
    })
    return dataItem
  })

  filteredData.value = filteredDataItems
}

function summariseVehicleCountData(dataContainer: Ref<VehicleCountData[] | null>) {
  if (!dataContainer.value) return
  dataContainer.value.forEach((dataItem) => {
    const countSummary = {} as FuelTypeCount
    dataItem.vehicle_data.forEach((vehicleDataItem) => {
      vehicleKeys.forEach((key) => {
        if (!countSummary[key]) countSummary[key] = 0
        countSummary[key] += vehicleDataItem.vehicle_count[key]
      })
    })
    dataItem.total_count = countSummary
  })
}

export function useAfirData(region?: RegionType, regionId?: string) {
  const afirElectricVehicleThreshold = 1.3
  const afirPluginHybridThreshold = 0.8
  const { data: vehicleCountData } = useVehicleCountDataFetch(region, regionId, false)

  const stationStore = useStationDataStore()
  // stationStore.initStations()

  const stationPowerSum = computed(() =>
    region === 'municipality'
      ? stationStore.getElectricPowerSumByMunicipality
      : stationStore.getElectricPowerSumByCounty
  )
  const stationPowerSumByCountry = computed(() => stationStore.getElectricPowerSumByCountry)

  const afirData = ref<null | AfirData[]>(null)

  const regionIdKeys = {
    municipality: 'municipality_code',
    county: 'county_code',
  }

  watch(
    stationPowerSum,
    (newValue) => {
      if (!newValue) return
      if (vehicleCountData.value && !afirData.value) parseAfirData()
    },
    { immediate: true }
  )

  watch(
    stationPowerSumByCountry,
    (newValue) => {
      if (!newValue) return
      if (vehicleCountData.value && !afirData.value) parseAfirData()
    },
    { immediate: true }
  )

  watch(
    vehicleCountData,
    (newValue) => {
      if (!newValue) return
      if (stationPowerSum.value && !afirData.value) parseAfirData()
    },
    { immediate: true }
  )

  function parseAfirData() {
    const regionIdKey = regionIdKeys[region as keyof typeof regionIdKeys]

    const dataArray = Array.isArray(vehicleCountData.value)
      ? vehicleCountData.value
      : [vehicleCountData.value]

    afirData.value = dataArray.map((vehicleCountDataItem) => {
      const regionKey = vehicleCountDataItem[regionIdKey as keyof VehicleCountData] as string

      let totalPower = (stationPowerSum.value && stationPowerSum.value[regionKey]?.power) || 0
      if (!regionIdKey) {
        totalPower = stationPowerSumByCountry.value || 0
      }

      function summariseVehicleCountData(prop: keyof FuelTypeCount) {
        const totalElectricVehicleCount = vehicleCountDataItem.vehicle_data.reduce(
          (acc, cur) =>
            cur.vehicle_type === 'personbil' || cur.vehicle_type === 'lätt lastbil'
              ? acc + cur.vehicle_count[prop]
              : acc,
          0
        )

        return totalElectricVehicleCount
      }

      const totalElectricVehicleCount = summariseVehicleCountData('el')

      const totalPluginHybridVehicleCount = summariseVehicleCountData('laddhybrid')

      const requiredPowerElectricVehicle = totalElectricVehicleCount * afirElectricVehicleThreshold

      const requiredPowerPluginHybridVehicle =
        totalPluginHybridVehicleCount * afirPluginHybridThreshold

      const isAfirCompliant =
        requiredPowerElectricVehicle + requiredPowerPluginHybridVehicle < totalPower

      const vehicleTypes = vehicleCountDataItem.vehicle_data.map((vehicleDataItem) => {
        const { vehicle_type: vehicleType } = vehicleDataItem
        const electricVehicleCount = vehicleDataItem.vehicle_count.el
        const pluginHybridVehicleCount = vehicleDataItem.vehicle_count.laddhybrid
        const powerPerElectricVehicle =
          (electricVehicleCount > 0 && totalPower / electricVehicleCount) || 0
        const isAfirCompliant = powerPerElectricVehicle > afirElectricVehicleThreshold
        return {
          vehicleType,
          electricVehicleCount,
          isAfirCompliant,
          pluginHybridVehicleCount,
        }
      })

      const returnObject = {
        year: vehicleCountDataItem.year,
        regionId: regionKey,
        totalPower,
        vehicleTypes,
        countyCode: vehicleCountDataItem.county_code,
        requiredPowerElectricVehicle,
        totalElectricVehicleCount,
        requiredPowerPluginHybridVehicle,
        totalPluginHybridVehicleCount,
        isAfirCompliant,
      }
      return returnObject
    })
  }

  return {
    data: afirData,
  }
}

export function useElectricityGridOwnerDataFetch(
  region?: RegionType,
  regionId?: string,
  getAll?: boolean
) {
  const { data, ...rest } = useStatisticsDataFetch<ElectricityGridOwner>(
    'electricityGrids',
    region,
    regionId,
    getAll
  )
  const ElectricityGridOwnerData = computed(() => {
    if (!data.value) return null

    if (Array.isArray(data.value)) {
      const sortedData = data.value.sort((a, b) => a.company.localeCompare(b.company))
      return groupElectricityOwnersByCompany(sortedData)
    } else {
      return groupElectricityOwnersByCompany([data.value])
    }
  }) as Ref<ElectricityGridOwner[]>

  // const ElectricityGridOwnerData = groupElectricityOwnersByCompany(data) as Ref<ElectricityGridOwner[]>
  return {
    data: ElectricityGridOwnerData,
    ...rest,
  }
}
function groupElectricityOwnersByCompany(
  electricityGridOwners: ElectricityGridOwner[]
): ElectricityGridOwner[] {
  const groupedOwners: ElectricityGridOwner[] = []
  electricityGridOwners.forEach((owner) => {
    const existingOwner = groupedOwners.find((m) => m.company === owner.company)
    if (existingOwner) {
      if (!existingOwner.areas.some((m) => m.koncession === owner.koncession))
        existingOwner.areas.push(owner)
      if (!existingOwner.municipalities.includes(owner.municipality_code))
        existingOwner.municipalities.push(owner.municipality_code)
    } else {
      owner.areas = [owner]
      owner.municipalities = [owner.municipality_code]
      groupedOwners.push(owner)
    }
  })

  return groupedOwners
}
export const vehicleKeys: (keyof VehicleTypeCountData['vehicle_count'])[] = [
  'el',
  'elhybrid',
  'bensin',
  'diesel',
  'etanol',
  'gas',
  'laddhybrid',
  'övriga',
]

export function useVehicleFleetOnPopulation(region: RegionType) {
  const { data: vehicleCountData } = useVehicleCountDataFetch<VehicleCountData[]>(region)
  const { data: populationData } = usePopulationDataFetch<PopulationData[]>(region)

  const returnData = ref<VehicleCountDataOnPopulation[]>([])
  watchEffect(parseData)
  function parseData() {
    if (!vehicleCountData.value || !populationData.value) return
    returnData.value = vehicleCountData.value.map((vehicleCountData) => {
      const returnObject = {
        ...vehicleCountData,
      } as any

      const populationCount = populationData.value.find(
        (m) => m.municipality_code === vehicleCountData.municipality_code
      )?.population

      vehicleKeys.forEach((key) => {
        const vehicleCount = vehicleCountData[key as keyof VehicleCountData] as number
        const populationPerVehicle =
          populationCount && vehicleCount > 0 ? populationCount / vehicleCount : 0
        returnObject[key] = populationPerVehicle
      })
      return returnObject
    })
  }
  return {
    data: returnData,
  }
}
export function useVehicleFleetOnFuelTypeRatio(region: RegionType) {
  const { filteredData } = useVehicleCountDataFetch<VehicleCountData[]>(region)

  const returnData = ref<VehicleCountData[]>([])
  watchEffect(parseData)
  function parseData() {
    if (!filteredData.value || filteredData.value.length === 0) return
    returnData.value = filteredData.value.map((vehicleCountData) => {
      const returnObject = {
        ...vehicleCountData,
      }

      function calcRatio(fuelTypeData: FuelTypeCount, totalCount: number) {
        vehicleKeys.forEach((key) => {
          let vehicleCount = fuelTypeData[key]
          if (!vehicleCount) vehicleCount = 0
          const fuelTypeRatio = vehicleCount / totalCount
          fuelTypeData[key] = fuelTypeRatio
        })
      }

      const totalVehicleCount = returnObject.vehicle_data.reduce(
        (acc, cur) => acc + cur.total_count,
        0
      )
      returnObject.vehicle_data.forEach((data) => calcRatio(data.vehicle_count, data.total_count))
      calcRatio(returnObject.total_count, totalVehicleCount)
      return returnObject
    })
  }
  return {
    data: returnData,
  }
}
